import { Component, Inject, Input } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { gtpRutValidator } from 'src/app/shared/validators/rut-validator';
import { rutValidate } from 'rut-helpers';

export interface ConfirmModel {
  title: string;
  subtitle: string;
  confirmButton: string;
  cancelButton: string;
  closeButton?: boolean;
  form: Array<{
    key: string;
    type?: 'text' | 'number' | 'date' | 'select' /*select*/ | 'textarea' | 'password' | 'rut' | 'email';  // por incluir: file, tel
    label?: string;
    placeholder?: string;
    required?: boolean;
    options?: Array<{ value: string, label: string }>;
    value?: any;
    /*fileValidation?: {
      maxSize?: number; // in MB
      allowedExtensions?: string[];
    };*/
  }>;
}

/*
contract:
{
  title: "", // -> title of the modal
  subtitle: "", // -> subtitle of the modal, by default is empty
  confirmButton: "", // -> text of the confirm button, if empty or false confirm button is not shown, if true is "Aceptar"
  cancelButton: "", // -> text of the cancel button, if empty or false cancel button is not shown, if true is "Cancelar"
  closeButton: true, // -> show or not the close button (button x in the top right), by default is true
  form: [
    {
      "key": "", // -> cant be empty, must be unique
      "type": "text", // -> text, number, date, select, textarea, file, password, rut // -> by default is text
      "label": "", // -> by default is the key
      "placeholder": "", // -> by default is the label
      "required": false, // -> true or false, by default true
      "options": [], // -> only for select type -> can be null or undefined
      "value": "", // -> initial value
      "min": 0, // -> only for number type -> can be null or undefined
      "max": 100, // -> only for number type -> can be null or undefined
      "fileValidation": { // -> only for file type, can be null or undefined // por ahora no incluir
        "maxSize": 5, // -> max size in MB, by default is 5
        "allowedExtensions": ["jpg", "jpeg", "png", "pdf"] // -> allowed extensions, by default is ["csv", "pdf"]
      },
    }
  ]
}
*/

@Component({
  selector: 'app-modal-form',
  templateUrl: './modal-form.component.html',
  styleUrls: ['./modal-form.component.scss']
})
export class ModalFormComponent {
  @Input() title: string = "";
  @Input() subtitle: string = "";
  @Input() confirmButton: string = "";
  @Input() cancelButton: string = "";
  @Input() closeButton: boolean = true;
  public form: any[] = [];

  public formComponent!: UntypedFormGroup;
  public messages: { text: string, isError: boolean, isSuccess: boolean }[] = [];

  constructor(public dialogRef: MatDialogRef<ModalFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmModel, 
    private formBuilder: UntypedFormBuilder
  ) {
    this.title = this.data?.title || '';
    this.subtitle = this.data?.subtitle || '';
    this.confirmButton = this.data?.confirmButton || '';
    this.cancelButton = this.data?.cancelButton || '';
    this.closeButton = this.data?.closeButton !== undefined ? this.data?.closeButton : true;
    if(this.data?.form && this.data?.form.length > 0){
      this.form = this.data?.form;
      this.createFormControls();
    } else {
      this.addMessage('Ocurrio un error al cargar el formulario', true, false);
    }
  }

  createFormControls(): void {
    this.formComponent = this.formBuilder.group({});
    this.form.forEach(control => {
      const validators = [];
      if (control.required) {
        validators.push(Validators.required);
      }
      if (control.min !== undefined) {
        validators.push(Validators.min(control.min));
      }
      if (control.max !== undefined) {
        validators.push(Validators.max(control.max));
      }
      if(control.type === 'rut') {
        validators.push(gtpRutValidator());
      }
      if(control.type === 'email') {
        validators.push(Validators.email);
      }
      /*if (control.fileValidation) { // por ahora no incluir
        if (control.fileValidation.maxSize) {
          validators.push(Validators.max(control.fileValidation.maxSize * 1024 * 1024)); // Convert MB to bytes
        }
      }*/
      this.formComponent.addControl(control.key, this.formBuilder.control(control.value || '', validators));
    });
  }

  //confirm
  confirm() {
    if(this.form.length === 0 || !this.form) {
      this.addMessage('No hay campos en el formulario', true, false);
      return;
    }

    // validar form
    if(!this.formComponent.valid) {
      this.formComponent.markAllAsTouched();
      this.addMessage('Complete correctamente los campos del formulario', true, false);
      return;
    }

    // validar de numeros
    if(!this.validateNumbers()) {
      this.addMessage('Los campos numéricos deben ser válidos', true, false);
      return;
    }

    // validar que el elemento seleccionado este entre las opciones
    const selectControls = this.form.filter(item => item.type === 'select');
    for(const control of selectControls) {
      const controlValue = this.formComponent.get(control.key)?.value;
      if(controlValue && control.options && control.options.length > 0) {
        const optionValues = control.options.map((option: { value: string, label: string }) => option.value);
        if(!optionValues.includes(controlValue)) {
          this.addMessage(`El valor seleccionado para ${control.label || control.key} no es válido`, true, false);
          return;
        }
      }
    }

    // validar tipos ruts
    const rutControls = this.form.filter(item => item.type === 'rut');
    for(const control of rutControls) {
      const controlValue = this.formComponent.get(control.key)?.value;
      if(controlValue && !rutValidate(controlValue)) {
        this.addMessage(`El RUT ingresado en ${control.label || control.key} no es válido`, true, false);
        return;
      }
    }

    // validar email
    /*const emailControls = this.form.filter(item => item.type === 'email');
    for(const control of emailControls) {
      const controlValue = this.formComponent.get(control.key)?.value;
      if(controlValue && !Validators.email(controlValue)) {
        this.addMessage(`El correo electrónico ingresado en ${control.label || control.key} no es válido`, true, false);
        return;
      }
    }*/

    const formData = this.formComponent.getRawValue();
    /*for(const key in formData) {
      // formatear fecha -> dd-mm-yyyy
      if(formData[key] instanceof Date) {
        const date = formData[key];
        formData[key] = `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()}`;
      }
      // formatear rut
      if(key === 'rut' && formData[key]) {
        formData[key] = this.formatterRut(formData[key]);
      }
    }*/

    // emit form data
    console.log('Form data:', formData);

    // cerrar modal
    this.dialogRef.close(formData);
  }

  formatterRut(rut: string): string { // por ahora no se usa pero utilizar la v2 de utilsService*
    if (!rut) {
      return '';
    }
    rut = rut.replace(/\./g, ''); // Eliminar puntos
    if (!rut.includes('-')) {
      // Agregar guion antes del último dígito si no tiene guion.
      rut = rut.slice(0, -1) + '-' + rut.slice(-1);
    }
    return rut;
  }

  validateNumbers(): boolean {
    for(const control of this.form) {
      if(control.type === 'number' && this.formComponent.get(control.key)?.value !== null) {
        const value = this.formComponent.get(control.key)?.value;
        if(isNaN(value)) {
          this.formComponent.get(control.key)?.setErrors({ 'invalidNumber': true });
          return false;
        }
      }
    }
    return true;
  }

  clearControl(key: string): void {
    if(this.formComponent.get(key)) {
      this.formComponent.get(key)?.setValue('');
      this.formComponent.get(key)?.markAsPristine();
      this.formComponent.get(key)?.markAsUntouched();
    }
  }

  //cancel
  close() {
    this.formComponent.reset();
    this.resetAll();
    this.dialogRef.close(false);
  }

  resetAll() {
    this.form = [];
    this.formComponent = this.formBuilder.group({});
  }

  addMessage(mensaje: string, isError: boolean, isSuccess: boolean) : void {
    this.messages.push({ text: mensaje, isError: isError, isSuccess: isSuccess });
  }

  removeMessage(index: number) {
    this.messages.splice(index, 1);
  }

}
